import { useNavigate } from "react-router-dom";
import { customerDetailPath } from "../../routes";
import { useFeatureFlag, FEATURE_FLAGS } from "../../contexts";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useEmptyStateTracking } from "../../hooks/useEmptyStateTracking";
import { getCurrencySymbol } from "../../utils/currencyUtils";
import { formatCompactOneDecimal } from "./constants";
import { ChevronRightIcon } from "./icons";

// Tabs are server-side filters (PAN-818 spec §7); "At Risk" became "High risk".
const FILTER_TABS = [
  { key: "all", label: "All" },
  { key: "high_risk", label: "High risk" },
  { key: "renewal", label: "Renewal" },
  { key: "expansion", label: "Expansion" },
  { key: "pilot", label: "Pilot" },
  { key: "communicated_churn", label: "Comm. churn" },
];

/**
 * Pill and left bar follow the account's level, computed server-side (PAN-818 spec §8):
 * critical and high red, medium amber, low grey. A communicated churn shows its own neutral
 * pill instead of a level. An account with no level is listed only through a motion; when that
 * motion is expansion it shows a green Opportunity pill (Ben, 2026-10-14), otherwise no pill.
 */
const LEVEL_STYLES = {
  critical: {
    barColor: "bg-red-600",
    badgeClass: "bg-red-50 text-red-700 border-red-300",
    label: "Critical",
  },
  high: {
    barColor: "bg-red-500",
    badgeClass: "bg-red-50 text-red-600 border-red-200",
    label: "High",
  },
  medium: {
    barColor: "bg-amber-500",
    badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
    label: "Medium",
  },
  low: {
    barColor: "bg-slate-300",
    badgeClass: "bg-slate-50 text-slate-600 border-slate-200",
    label: "Low",
  },
};
const COMM_CHURN_STYLE = {
  barColor: "bg-slate-400",
  badgeClass: "bg-slate-100 text-slate-600 border-slate-300",
  label: "Comm. churn",
};
const OPPORTUNITY_STYLE = {
  barColor: "bg-emerald-500",
  badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
  label: "Opportunity",
};
const NO_LEVEL_STYLE = {
  barColor: "bg-slate-200",
  badgeClass: null,
  label: null,
};

const getAccountStyle = (account) => {
  if (account.is_comm_churn) return COMM_CHURN_STYLE;
  if (LEVEL_STYLES[account.level]) return LEVEL_STYLES[account.level];
  return (account.motions || []).includes("expansion")
    ? OPPORTUNITY_STYLE
    : NO_LEVEL_STYLE;
};

// Signal chips list most serious first and collapse into "+N more" after MAX_SIGNAL_CHIPS
// (spec §8). They keep one neutral colour: the bar and the pill already show the level
// (Ben, 2026-10-13); each chip's own level is its tooltip.
const SIGNAL_LEVEL_ORDER = ["critical", "high", "medium", "low", "none"];
const SIGNAL_LEVEL_LABELS = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};
const MAX_SIGNAL_CHIPS = 3;

const levelRank = (level) => {
  const index = SIGNAL_LEVEL_ORDER.indexOf(level);
  return index === -1 ? SIGNAL_LEVEL_ORDER.length : index;
};

// Why an account is on the Expansion tab must be visible on its row. An expansion signal has no
// risk level, so on that tab it leads instead of hiding in "+N more". An account there through
// the "has expansion potential" flag alone has no signal to show; the row doesn't carry the flag,
// but `motions` has "expansion" for either cause, so no expansion signal means it is the flag.
const EXPANSION_SIGNAL_TYPES = ["expansion_discussion"];
const EXPANSION_POTENTIAL_CHIP = {
  signal_type: "expansion_potential",
  title: "Expansion potential",
  level: "none",
};
const isExpansionChip = (chip) =>
  chip.signal_type === EXPANSION_POTENTIAL_CHIP.signal_type ||
  EXPANSION_SIGNAL_TYPES.includes(chip.signal_type);

const getChips = (account, filter) => {
  const chips = (account.active_signals || []).filter((s) => s.title);
  if (
    (account.motions || []).includes("expansion") &&
    !chips.some((c) => EXPANSION_SIGNAL_TYPES.includes(c.signal_type))
  ) {
    chips.push(EXPANSION_POTENTIAL_CHIP);
  }
  const rank = (chip) =>
    filter === "expansion" && isExpansionChip(chip)
      ? -1
      : levelRank(chip.level);
  return chips.sort((a, b) => rank(a) - rank(b));
};

// Pilot rows carry subscription mrr=0 by design (Strict-NRR split); their value lives on
// pilot_mrr (currency-safe from the BE). Fall back to mrr when the pilot field is absent so the
// FE is safe to deploy before the BE (PAN-289). Mirrors the #160/#166 pilot-MRR pattern.
const formatRevenue = (account, isPilot = false, revenueUnit = "MRR") => {
  const usePilot = isPilot && account.pilot_mrr != null;
  const sym = getCurrencySymbol(
    (usePilot ? account.pilot_mrr_currency : account.mrr_currency) || "USD",
  );
  const monthlyAmount = parseFloat(
    (usePilot ? account.pilot_mrr : account.mrr) || 0,
  );
  const amount = revenueUnit === "ARR" ? monthlyAmount * 12 : monthlyAmount;
  return `${sym}${formatCompactOneDecimal(amount)}`;
};

// Signal labels are produced by the backend (SIGNAL_DISPLAY_REGISTRY → s.title), the same
// source as the customer-details Signals card, so the two surfaces can't drift.
const AccountRow = ({ account, filter, isLast, onNavigate, revenueUnit }) => {
  const style = getAccountStyle(account);
  const signals = getChips(account, filter);
  const shown = signals.slice(0, MAX_SIGNAL_CHIPS);
  const hidden = signals.length - shown.length;
  const pilotEnabled = useFeatureFlag(FEATURE_FLAGS.PILOT_PIPELINE_SUPPORT);
  const isPilot = pilotEnabled && account.subscription_status === "pilot";

  return (
    <button
      type="button"
      title={account.reasons || undefined}
      onClick={() => onNavigate(account.account_id)}
      className={`flex w-full cursor-pointer items-center gap-4 bg-transparent px-6 py-[18px] text-left transition-colors hover:bg-slate-50 ${
        !isLast ? "border-b border-slate-100" : ""
      } border-0`}
    >
      <div
        data-testid="priority-level-bar"
        className={`h-12 w-1.5 flex-shrink-0 rounded-full ${style.barColor}`}
      />
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex items-center gap-2.5">
          <span className="text-sm font-bold text-slate-800">
            {account.account_name}
          </span>
          {isPilot && (
            <span className="rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
              Pilot
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {shown.map((signal, i) => (
            <span
              key={`signal-${i}`}
              title={SIGNAL_LEVEL_LABELS[signal.level]}
              className="rounded bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-600"
            >
              {signal.title}
            </span>
          ))}
          {hidden > 0 && (
            <span className="rounded px-2 py-0.5 text-xs text-slate-500">
              +{hidden} more
            </span>
          )}
          {signals.length === 0 && (
            <span className="text-xs text-slate-400">No risk signals</span>
          )}
        </div>
      </div>
      {style.label && (
        <div
          className={`flex-shrink-0 rounded-full border px-3.5 py-[5px] text-xs font-bold ${style.badgeClass}`}
        >
          {style.label}
        </div>
      )}
      <div className="min-w-[120px] flex-shrink-0 text-right">
        <div className="text-[11px] font-semibold uppercase tracking-[0.3px] text-slate-400">
          {isPilot ? `Pilot ${revenueUnit}` : revenueUnit}
        </div>
        <div className="text-lg font-extrabold tracking-[-0.5px] text-slate-800">
          {formatRevenue(account, isPilot, revenueUnit)}
        </div>
        {account.motion_line && (
          <div className="mt-0.5 text-xs text-slate-500">
            {account.motion_line}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 text-slate-300">
        <ChevronRightIcon />
      </div>
    </button>
  );
};

const LoadingSkeleton = () => (
  <div data-testid="priority-loading" className="animate-pulse px-6 py-5">
    {[1, 2, 3].map((i) => (
      <div key={i} className="mb-4 flex items-center gap-4">
        <div className="h-12 w-1.5 rounded-full bg-slate-200" />
        <div className="flex-1">
          <div className="mb-2 h-4 w-40 rounded bg-slate-200" />
          <div className="h-3 w-60 rounded bg-slate-100" />
        </div>
        <div className="h-6 w-16 rounded-full bg-slate-100" />
        <div className="h-8 w-16 rounded bg-slate-100" />
      </div>
    ))}
  </div>
);

export const PriorityAccounts = ({
  accounts,
  loading,
  filter,
  onFilterChange,
  onLoadMore,
  loadingMore = false,
  revenueUnit = "MRR",
}) => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const pilotEnabled = useFeatureFlag(FEATURE_FLAGS.PILOT_PIPELINE_SUPPORT);
  const tabs = pilotEnabled
    ? FILTER_TABS
    : FILTER_TABS.filter((tab) => tab.key !== "pilot");
  const results = accounts?.results ?? [];
  const total = accounts?.count ?? results.length;
  const initialLoad = accounts === null && loading;

  useEmptyStateTracking("priority_accounts", {
    isEmpty: results.length === 0,
    isLoading: initialLoad || loading,
    properties: { filter },
  });

  return (
    <div className="mt-6">
      <div className="overflow-hidden rounded-[14px] border border-[#e8ecf0] bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
          <div>
            <div className="text-base font-bold text-slate-800">
              Priority Accounts
            </div>
            <div className="mt-0.5 text-xs text-slate-400">
              Ranked by risk level × revenue at stake × urgency — your strategic
              focus for today
            </div>
          </div>
          <div
            className="flex items-center gap-2"
            role="group"
            aria-label="Filter by account category"
          >
            {tabs.map((tab) => (
              <button
                key={tab.key}
                aria-pressed={filter === tab.key}
                type="button"
                onClick={() => onFilterChange(tab.key)}
                className={`cursor-pointer rounded-full border px-3.5 py-[5px] text-xs font-semibold transition-colors ${
                  filter === tab.key
                    ? "border-slate-800 bg-slate-800 text-white"
                    : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {initialLoad && <LoadingSkeleton />}

        {!initialLoad && !loading && results.length === 0 && (
          <div className="px-6 py-12 text-center text-sm text-slate-400">
            No priority accounts to display
          </div>
        )}

        {results.length > 0 && (
          <div
            className={
              loading
                ? "pointer-events-none opacity-50 transition-opacity"
                : "transition-opacity"
            }
          >
            {results.map((account, idx) => (
              <AccountRow
                key={account.account_id}
                account={account}
                filter={filter}
                isLast={idx === results.length - 1 && !accounts?.next}
                revenueUnit={revenueUnit}
                onNavigate={(id) => {
                  // Dashboard→detail click-through isn't derivable from
                  // $pageview (no referrer semantics in the SPA).
                  trackEvent("priority_account_clicked", {
                    account_id: id,
                    position: idx,
                    filter,
                  });
                  navigate(customerDetailPath(id));
                }}
              />
            ))}
            {accounts?.next && onLoadMore && (
              <div className="flex items-center justify-between px-6 py-4 text-xs text-slate-400">
                <span>
                  Showing {results.length} of {total}
                </span>
                <button
                  type="button"
                  disabled={loadingMore}
                  onClick={onLoadMore}
                  className="cursor-pointer rounded-full border border-slate-200 bg-white px-3.5 py-[5px] text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-default disabled:opacity-50"
                >
                  {loadingMore ? "Loading…" : "Show more"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
